/**
 * SessionPrepCard — AI-generated session prep notes card.
 *
 * Teal left accent border, sparkles icon header, list of PrepNoteRow items.
 * Hidden entirely when notes array is empty.
 * Consent-gated by caller (hidden when can_view_history=false).
 *
 * Accessibility: Wraps all rows in a single accessible region.
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { GlassCard } from '../../glass/GlassCard';
import { PrepNoteRow } from './PrepNoteRow';

type NoteType = 'improving' | 'persistent' | 'suggested';

interface PrepNote {
  type: NoteType;
  text: string;
}

interface SessionPrepCardProps {
  notes: PrepNote[];
  t: TFunction<'practitioner'>;
}

const TEAL_LEFT_BORDER: ViewStyle = {
  borderLeftWidth: 3,
  borderLeftColor: '#14B8A6',
};

export const SessionPrepCard: React.FC<SessionPrepCardProps> = ({ notes, t }) => {
  if (notes.length === 0) return null;

  return (
    <GlassCard withChromeBorder={false} noPadding={true} style={TEAL_LEFT_BORDER}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.iconBadge}>
          <Ionicons name="sparkles" size={14} color="#0D9488" />
        </View>
        <Text style={styles.title}>{t('prep_card.title')}</Text>
      </View>

      {/* Note rows */}
      <View
        style={styles.noteList}
        accessible={true}
        accessibilityLabel={t('prep_card.title')}
      >
        {notes.map((note, index) => (
          <PrepNoteRow key={`${note.type}-${index}`} type={note.type} text={note.text} t={t} />
        ))}
      </View>
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  } as ViewStyle,
  iconBadge: {
    width: 24,
    height: 24,
    borderRadius: 8,
    backgroundColor: 'rgba(20, 184, 166, 0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
  },
  noteList: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 10,
  },
});
